import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isEntryEditable } from '@h2h/shared';
import { type Fixture, type Prediction } from '@h2h/shared';
import { Placeholder } from '../../../components/ui/Placeholder';
import { ArrowRight, ChevronLeft, ChevronRight, LayoutGrid } from '../../../components/ui/icons';
import { useLeagueRounds, useRoundFixtures, useMyEntry } from '../../../hooks/useQueries';
import { useHaptics } from '../../../hooks/useHaptics';
import { cn } from '../../../lib/cn';
import { roundPhaseLabel, slateCardSummary } from '../../../lib/footballRoundUi';
import { signedPoints } from '../../../lib/format';
import { LiveFixtureSheet } from '../../match-detail/LiveFixtureSheet';
import { dayHeading, useNow } from '../helpers';
import { FixtureRow } from './FixtureRow';
import { RoundRecap, roundDateRange } from './RoundRecap';
import { gradePick } from './gradePick';

// ── Fixtures tab — full round schedule + your card history per match ─────────
export function FixturesTab({
  leagueId,
  currentRoundId,
  uid,
  nationalTeams = false
}: {
  leagueId: string;
  currentRoundId: string | null;
  uid: string | null;
  nationalTeams?: boolean;
}) {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const now = useNow(true);
  const [liveFixtureId, setLiveFixtureId] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const roundParam = params.get('round');
  const { data: rounds } = useLeagueRounds(leagueId);

  const ordered = useMemo(
    () => [...(rounds ?? [])].sort((a, b) => a.firstKickoffAt - b.firstKickoffAt),
    [rounds]
  );
  // Open on a deep-linked round (e.g. a tapped knockout stage), falling back to
  // the league's current round, then the latest.
  const initialIdx = useMemo(() => {
    const target = roundParam ?? currentRoundId;
    const i = ordered.findIndex((r) => r.roundId === target);
    return i >= 0 ? i : Math.max(0, ordered.length - 1);
  }, [ordered, currentRoundId, roundParam]);

  const [idx, setIdx] = useState<number | null>(null);
  const activeIdx = idx ?? initialIdx;
  const selected = ordered[activeIdx];
  const selectedRoundId = selected?.roundId ?? currentRoundId;

  // Keep the live listener attached while a round is settling too, so points tick
  // in as fixtures finish and the tab doubles as a live pick tracker, not just a
  // post-hoc history.
  const live =
    selected?.status === 'OPEN' || selected?.status === 'LOCKING' || selected?.status === 'SETTLING';
  const { data: fixtures } = useRoundFixtures(leagueId, selectedRoundId, live);
  const { data: entry } = useMyEntry(leagueId, selectedRoundId, uid);
  // fixtureId → my prediction, so each row can show what I backed plus the points
  // it earned (settled) or is on track for (live) — the tab is also pick history.
  const myPicks = useMemo(() => {
    const map = new Map<string, Prediction>();
    for (const p of entry?.predictions ?? []) map.set(p.fixtureId, p);
    return map;
  }, [entry]);

  // Round recap across my cards: how many have settled (of those I built), the
  // points they banked, and how many were a PERFECT MATCH (base hit plus ≥1 boost,
  // every boost landed). The strip reads as a scored history — progress + points —
  // and only flips to the perfect-card celebration when one actually lands.
  const recap = useMemo(() => {
    let perfect = 0;
    let settled = 0;
    let points = 0;
    const built = myPicks.size;
    for (const f of fixtures ?? []) {
      const pick = myPicks.get(f.fixtureId);
      if (!pick || f.status !== 'SETTLED' || !f.result) continue;
      const g = gradePick(pick, f.result);
      settled++;
      points += g.points;
      if (g.perfect) perfect++;
    }
    return settled > 0 ? { perfect, settled, built, points } : null;
  }, [fixtures, myPicks]);

  // Group fixtures by calendar day.
  const days = useMemo(() => {
    const map = new Map<string, Fixture[]>();
    for (const f of [...(fixtures ?? [])].sort((a, b) => a.kickoffAt - b.kickoffAt)) {
      const key = dayHeading(f.kickoffAt);
      (map.get(key) ?? map.set(key, []).get(key)!).push(f);
    }
    return [...map.entries()];
  }, [fixtures]);

  const canPrev = activeIdx > 0;
  const canNext = activeIdx < ordered.length - 1;
  const hasEntry = !!entry;
  const cardCount = entry?.predictions?.length ?? 0;
  const roundScore = entry?.roundScore;
  const phase = roundPhaseLabel(selected?.status);
  const summary = useMemo(
    () => slateCardSummary(fixtures ?? [], new Set(myPicks.keys()), now),
    [fixtures, myPicks, now]
  );
  const roundOpen = selected?.status === 'OPEN';
  const showBuildCta = roundOpen && selectedRoundId === currentRoundId && cardCount === 0;

  return (
    <>
      {/* round selector */}
      <div className="mt-3 rounded-[18px] border border-black/10 bg-wash px-3 py-3 shadow-tile">
        <div className="flex items-center justify-between">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => { impact('selection'); setIdx(activeIdx - 1); }}
            className={cn('tap grid h-9 w-9 place-items-center rounded-full bg-white shadow-soft', !canPrev && 'opacity-30')}
          >
            <ChevronLeft width={20} height={20} strokeWidth={2.4} className="text-ink2" />
          </button>
          <div className="text-center">
            <div className="text-[15px] font-extrabold tracking-[-0.01em] text-ink">{selected?.label ?? 'This round'}</div>
            <div className="text-[12px] font-semibold text-ink2">
              {selected ? roundDateRange(selected) : ''}
              {phase ? ` · ${phase}` : ''}
              {hasEntry && roundScore != null ? (
                <>
                  {' · '}
                  <span className="num font-bold text-ink">{signedPoints(roundScore)} pts</span>
                </>
              ) : null}
            </div>
            {hasEntry && (fixtures?.length ?? 0) > 0 && (
              <div className="mt-1 text-[11px] font-semibold text-ink2">
                <span className="num font-bold text-ink">{summary.built}</span> card{summary.built === 1 ? '' : 's'} built
                {summary.open > 0 && (
                  <>
                    {' · '}
                    <span className="num font-bold text-accent-ink">{summary.open}</span> still open
                  </>
                )}
                {summary.missed > 0 && (
                  <>
                    {' · '}
                    <span className="num font-bold text-ink">{summary.missed}</span> missed
                  </>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => { impact('selection'); setIdx(activeIdx + 1); }}
            className={cn('tap grid h-9 w-9 place-items-center rounded-full bg-white shadow-soft', !canNext && 'opacity-30')}
          >
            <ChevronRight width={20} height={20} strokeWidth={2.4} className="text-ink" />
          </button>
        </div>
      </div>

      {showBuildCta && (
        <button
          type="button"
          onClick={() => {
            impact('medium');
            navigate(`/play/${leagueId}${selectedRoundId ? `?round=${selectedRoundId}` : ''}`);
          }}
          className="tap mt-3 flex w-full items-center justify-center gap-2 rounded-[16px] bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] py-[14px] shadow-[0_16px_30px_-16px_rgba(11,160,120,0.55)]"
        >
          <span className="text-[14px] font-extrabold text-white">Build your cards</span>
          <ArrowRight width={15} height={15} strokeWidth={2.8} className="text-white" />
        </button>
      )}

      {recap && (
        <RoundRecap perfect={recap.perfect} settled={recap.settled} built={recap.built} points={recap.points} />
      )}

      {!hasEntry && selected && days.length > 0 && (
        <div className="mt-3 rounded-[16px] border border-black/10 bg-wash px-[14px] py-[12px] text-center shadow-tile">
          <p className="text-[13px] font-bold text-ink">You didn&apos;t enter this round</p>
          <p className="mt-[4px] text-[12px] font-medium text-ink2">Browse the schedule — step to a round you played to see your card history.</p>
        </div>
      )}

      {hasEntry && cardCount === 0 && roundOpen && (
        <div className="mt-3 rounded-[16px] border border-dashed border-black/14 bg-wash px-[14px] py-[12px] text-center">
          <p className="text-[13px] font-semibold text-ink2">No cards built yet — build on any fixtures you like before they kick off.</p>
        </div>
      )}

      {days.length === 0 ? (
        <Placeholder Icon={LayoutGrid} title="No fixtures yet" body="The schedule for this round hasn’t been published. Check back soon." />
      ) : (
        days.map(([day, fs]) => (
          <div key={day}>
            <div className="mb-2 mt-5 flex items-center gap-3 border-b border-black/10 pb-2">
              <span className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-ink">{day}</span>
              <span className="h-px flex-1 bg-black/8" />
            </div>
            <div className="flex flex-col gap-2.5 md:grid md:grid-cols-2 md:gap-x-3">
              {fs.map((f) => (
                <FixtureRow
                  key={f.fixtureId}
                  fixture={f}
                  pick={myPicks.get(f.fixtureId)}
                  onOpen={() => {
                    impact('light');
                    const editable =
                      !!selected &&
                      f.status === 'SCHEDULED' &&
                      now < f.kickoffAt &&
                      isEntryEditable(selected, [], now);
                    if (editable) {
                      // Stay on the league hub and let the card-builder sheet animate
                      // in — a pathname-preserving param swap avoids a NavStack fade
                      // fighting the sheet on the way open.
                      const p = new URLSearchParams(params);
                      p.delete('tab');
                      if (selectedRoundId) p.set('round', selectedRoundId);
                      else p.delete('round');
                      p.set('fixture', f.fixtureId);
                      setParams(p, { replace: true });
                      return;
                    }
                    // Live / settled / locked — slide-up sheet over the hub (same as
                    // Compete) so back is just a dismiss, not a NavStack pop that
                    // remounts this tab underneath.
                    if (selected) setLiveFixtureId(f.fixtureId);
                  }}
                />
              ))}
            </div>
          </div>
        ))
      )}

      {selected && (
        <LiveFixtureSheet
          openFixtureId={liveFixtureId}
          onClose={() => setLiveFixtureId(null)}
          leagueId={leagueId}
          round={selected}
          fixtures={fixtures ?? []}
          entry={entry}
          nationalTeams={nationalTeams}
        />
      )}
    </>
  );
}
